import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useConfirm } from '../shared/Confirm';
import { to12h } from '../../utils/time';

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS_CAB = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

const TIPOS = {
  abierto:    { label: 'Disponible',        color: '#16a34a' },
  modificado: { label: 'Horario modificado', color: '#f59e0b' },
  cerrado:    { label: 'Cerrado',            color: '#ef4444' },
  bloqueos:   { label: 'Horas bloqueadas',   color: '#475569' },
};

const MOTIVOS = ['Festivo', 'Evento especial', 'Capacitación', 'Vacaciones', 'Mantenimiento', 'Otro'];

const aFecha = (y, m, d) => `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

export default function CalendarExceptions({ businessId, branchId }) {
  const hoy = new Date();
  const [anio, setAnio]           = useState(hoy.getFullYear());
  const [mes, setMes]             = useState(hoy.getMonth());
  const [excepciones, setExcep]   = useState([]);
  const [cargando, setCargando]   = useState(true);
  const [seleccion, setSeleccion] = useState(null);   // 'YYYY-MM-DD'
  const [tipo, setTipo]           = useState('cerrado');
  const [apertura, setApertura]   = useState('10:00');
  const [cierre, setCierre]       = useState('18:00');
  const [bloqueos, setBloqueos]   = useState([{ inicio: '11:00', fin: '12:00' }]);
  const [motivo, setMotivo]       = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError]         = useState('');
  const [ok, setOk]               = useState('');
  const confirmar = useConfirm();

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await api.get(`/businesses/${businessId}/exceptions`, {
        params: { year: anio, month: mes + 1, branch_id: branchId || undefined },
      });
      setExcep(res.data);
    } catch { setExcep([]); }
    finally { setCargando(false); }
  }, [businessId, branchId, anio, mes]);

  useEffect(() => { cargar(); }, [cargar]);

  const avisar = (msg, esError = false) => {
    if (esError) { setError(msg); setTimeout(() => setError(''), 5000); }
    else          { setOk(msg);   setTimeout(() => setOk(''), 4000); }
  };

  const excepcionDe = fecha => excepciones.find(e => e.fecha === fecha);

  const seleccionarDia = fecha => {
    setSeleccion(fecha);
    setError(''); setOk('');
    const e = excepcionDe(fecha);
    if (e) {
      setTipo(e.tipo);
      setApertura(e.hora_apertura || '10:00');
      setCierre(e.hora_cierre || '18:00');
      setBloqueos(e.bloqueos?.length ? e.bloqueos : [{ inicio: '11:00', fin: '12:00' }]);
      setMotivo(e.motivo || '');
    } else {
      setTipo('cerrado');
      setApertura('10:00'); setCierre('18:00');
      setBloqueos([{ inicio: '11:00', fin: '12:00' }]);
      setMotivo('');
    }
  };

  const guardar = async () => {
    if (!seleccion) return;
    setGuardando(true);
    try {
      await api.post(`/businesses/${businessId}/exceptions`, {
        fecha: seleccion, tipo,
        hora_apertura: apertura, hora_cierre: cierre,
        bloqueos, motivo: motivo || null,
        branch_id: branchId || null,
      });
      avisar(tipo === 'abierto' ? 'El día volvió a su horario normal' : 'Excepción guardada');
      setSeleccion(null);
      cargar();
    } catch (err) {
      avisar(err.response?.data?.error || 'Error guardando la excepción', true);
    } finally { setGuardando(false); }
  };

  const eliminar = async (id) => {
    if (!await confirmar({
      titulo: 'Eliminar excepción',
      mensaje: '¿Eliminar esta excepción?',
      detalle: 'El día volverá a su horario normal.',
      aceptar: 'Eliminar',
      peligro: true,
    })) return;
    try {
      await api.delete(`/businesses/${businessId}/exceptions/${id}`);
      avisar('Excepción eliminada');
      cargar();
    } catch { avisar('No se pudo eliminar', true); }
  };

  const cambiarMes = dir => {
    let m = mes + dir, y = anio;
    if (m < 0) { m = 11; y--; } else if (m > 11) { m = 0; y++; }
    setMes(m); setAnio(y); setSeleccion(null);
  };

  const irHoy = () => { setAnio(hoy.getFullYear()); setMes(hoy.getMonth()); setSeleccion(null); };

  // Celdas del mes empezando en lunes
  const primerDia = new Date(anio, mes, 1).getDay();          // 0=Dom
  const offset = (primerDia + 6) % 7;                          // lunes primero
  const totalDias = new Date(anio, mes + 1, 0).getDate();
  const celdas = [];
  for (let i = 0; i < offset; i++) celdas.push(null);
  for (let d = 1; d <= totalDias; d++) celdas.push(d);

  const detalleTexto = e => {
    if (e.tipo === 'cerrado')    return 'Día completo';
    if (e.tipo === 'modificado') return `${to12h(e.hora_apertura)} - ${to12h(e.hora_cierre)}`;
    if (e.tipo === 'bloqueos')   return (e.bloqueos || []).map(b => `${to12h(b.inicio)} - ${to12h(b.fin)}`).join(' · ');
    return '—';
  };

  const inputHora = { padding: '7px 9px', fontSize: '0.85rem' };

  return (
    <div>
      {ok    && <div className="alert alert-success" style={{ marginBottom: '1rem' }}>{ok}</div>}
      {error && <div className="alert alert-danger"  style={{ marginBottom: '1rem' }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: seleccion ? '1.3fr 1fr' : '1fr', gap: '1.25rem', alignItems: 'start' }}>
        {/* ── Calendario ── */}
        <div className="card" style={{ padding: '1.25rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 8 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-ghost" style={{ padding: '4px 10px' }} onClick={() => cambiarMes(-1)}>‹</button>
              <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: '0.8rem' }} onClick={irHoy}>Hoy</button>
            </div>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>{MESES[mes]} {anio}</div>
            <button className="btn btn-ghost" style={{ padding: '4px 10px' }} onClick={() => cambiarMes(1)}>›</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 6 }}>
            {DIAS_CAB.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--color-text-muted)', fontWeight: 600 }}>{d}</div>
            ))}
          </div>

          {cargando ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-text-muted)' }}>Cargando...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
              {celdas.map((d, i) => {
                if (!d) return <div key={i} />;
                const fecha = aFecha(anio, mes, d);
                const e = excepcionDe(fecha);
                const activo = seleccion === fecha;
                const esHoy = fecha === aFecha(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
                return (
                  <div
                    key={i}
                    onClick={() => seleccionarDia(fecha)}
                    style={{
                      position: 'relative', textAlign: 'center', padding: '9px 0',
                      borderRadius: 8, cursor: 'pointer', fontSize: '0.88rem',
                      fontWeight: activo || esHoy ? 700 : 500,
                      background: activo ? 'var(--color-gold)' : 'transparent',
                      color: activo ? '#fff' : 'var(--color-text)',
                      border: `1px solid ${activo ? 'var(--color-gold)' : esHoy ? 'var(--color-gold)' : 'transparent'}`,
                    }}
                  >
                    {d}
                    {e && (
                      <span style={{
                        position: 'absolute', bottom: 3, left: '50%', transform: 'translateX(-50%)',
                        width: 6, height: 6, borderRadius: '50%',
                        background: activo ? '#fff' : (TIPOS[e.tipo]?.color || '#888'),
                      }} />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
            {Object.entries(TIPOS).map(([k, v]) => (
              <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: v.color }} />
                {v.label}
              </span>
            ))}
          </div>
        </div>

        {/* ── Editor del día seleccionado ── */}
        {seleccion && (
          <div className="card" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 700 }}>
                {Number(seleccion.split('-')[2])} de {MESES[mes]}, {anio}
              </div>
              <button className="btn btn-ghost" style={{ padding: '2px 8px' }} onClick={() => setSeleccion(null)}>✕</button>
            </div>

            {[
              ['abierto',    'Día abierto (horario normal)'],
              ['modificado', 'Modificar horario para este día'],
              ['cerrado',    'Cerrar todo el día'],
              ['bloqueos',   'Bloquear horas específicas'],
            ].map(([valor, etiqueta]) => (
              <label key={valor} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 0', cursor: 'pointer', fontSize: '0.87rem' }}>
                <input type="radio" name="tipoExcepcion" checked={tipo === valor} onChange={() => setTipo(valor)} />
                {etiqueta}
              </label>
            ))}

            {tipo === 'modificado' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 12 }}>
                <div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 3 }}>Apertura</div>
                  <input type="time" className="form-input" style={inputHora} value={apertura} onChange={e => setApertura(e.target.value)} />
                </div>
                <div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 3 }}>Cierre</div>
                  <input type="time" className="form-input" style={inputHora} value={cierre} onChange={e => setCierre(e.target.value)} />
                </div>
              </div>
            )}

            {tipo === 'bloqueos' && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, marginBottom: 8 }}>Horas bloqueadas</div>
                {bloqueos.map((b, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <input type="time" className="form-input" style={{ ...inputHora, flex: 1 }} value={b.inicio}
                      onChange={e => setBloqueos(bs => bs.map((x, j) => j === i ? { ...x, inicio: e.target.value } : x))} />
                    <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                    <input type="time" className="form-input" style={{ ...inputHora, flex: 1 }} value={b.fin}
                      onChange={e => setBloqueos(bs => bs.map((x, j) => j === i ? { ...x, fin: e.target.value } : x))} />
                    <button
                      className="btn btn-ghost" style={{ padding: '4px 8px', color: '#ef4444' }}
                      disabled={bloqueos.length === 1}
                      onClick={() => setBloqueos(bs => bs.filter((_, j) => j !== i))}
                    >🗑</button>
                  </div>
                ))}
                <button className="btn btn-ghost" style={{ fontSize: '0.8rem' }}
                  onClick={() => setBloqueos(bs => [...bs, { inicio: '15:00', fin: '16:00' }])}>
                  + Agregar bloqueo
                </button>
              </div>
            )}

            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', marginBottom: 3 }}>Motivo (opcional)</div>
              <select className="form-select" style={inputHora} value={motivo} onChange={e => setMotivo(e.target.value)}>
                <option value="">Sin motivo</option>
                {MOTIVOS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>

            <button className="btn btn-primary" style={{ width: '100%', marginTop: 16 }} onClick={guardar} disabled={guardando}>
              {guardando ? 'Guardando...' : 'Guardar excepción'}
            </button>
          </div>
        )}
      </div>

      {/* ── Lista de excepciones del mes ── */}
      <div className="card" style={{ marginTop: '1.25rem' }}>
        <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--color-border)', fontWeight: 700 }}>
          Lista de excepciones
        </div>
        {excepciones.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.87rem' }}>
            No hay excepciones en {MESES[mes]} {anio}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Fecha</th><th>Estado</th><th>Detalle</th><th>Motivo</th><th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {excepciones.map(e => (
                  <tr key={e.id}>
                    <td>{e.fecha.split('-').reverse().join('/')}</td>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: TIPOS[e.tipo]?.color || '#888' }} />
                        {TIPOS[e.tipo]?.label || e.tipo}
                      </span>
                    </td>
                    <td style={{ fontSize: '0.83rem' }}>{detalleTexto(e)}</td>
                    <td style={{ fontSize: '0.83rem', color: 'var(--color-text-muted)' }}>{e.motivo || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost" style={{ padding: '3px 8px' }} title="Editar"
                          onClick={() => { const [y, m] = e.fecha.split('-').map(Number); setAnio(y); setMes(m - 1); seleccionarDia(e.fecha); }}>✏️</button>
                        <button className="btn btn-ghost" style={{ padding: '3px 8px', color: '#ef4444' }} title="Eliminar"
                          onClick={() => eliminar(e.id)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: 'var(--color-surface)', fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
        ℹ️ Las excepciones tienen prioridad sobre los horarios semanales.
      </div>
    </div>
  );
}
